'use client'

export interface GameState {
  player: { x: number, y: number, jumping: boolean, jumpHeight: number, boost?: number }
  target: { x: number, y: number, jumping: boolean, jumpHeight: number }
  obstacles: Array<{ x: number, y: number, width: number, height: number }>
  collectibles: Array<{ x: number, y: number, collected: boolean }>
  coins: Array<{ x: number, y: number, collected: boolean }>
  collectEffects: Array<{ x: number, y: number, opacity: number, scale: number, time: number }>
  distance: number
  score: number
  camera: { x: number, shouldFollow: boolean }
  hugAnimation: { active: boolean, time: number, heartsSpawned: boolean }
  runningTime: number
}

export interface GameConfig {
  playerSpeed: number
  targetSpeed: number
  accelerationMultiplier: number
  jumpHeight: number
  jumpSpeed: number
  obstacleSpeed: number
  collectibleSpeed: number
  obstacleSpawnRate: number
  collectibleSpawnRate: number
  cameraFollowThreshold: number
  cameraSpeed: number
  winDistance: number
  collectiblePoints: number
  victoryBonus: number
}

export const DEFAULT_CONFIG: GameConfig = {
  playerSpeed: 1,
  targetSpeed: 1.5,
  accelerationMultiplier: 3,
  jumpHeight: 55, // Hauteur de saut réduite pour un ressenti plus naturel
  jumpSpeed: 8,
  obstacleSpeed: 2,
  collectibleSpeed: 2,
  obstacleSpawnRate: 0.003,
  collectibleSpawnRate: 0.006,
  cameraFollowThreshold: 100,
  cameraSpeed: 0.08,
  winDistance: 50,
  collectiblePoints: 10,
  victoryBonus: 100
}

export class GameEngine {
  private config: GameConfig

  constructor(config: Partial<GameConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
  }

  // Mise à jour du joueur
  updatePlayer(
    state: GameState,
    keysPressed: Set<string>,
    isAccelerating: boolean = false
  ): void {
    // Mouvement automatique
    let speed = this.config.playerSpeed
    if (isAccelerating) speed += this.config.accelerationMultiplier
    if (state.player.boost && state.player.boost > 0) {
      speed += this.config.accelerationMultiplier * 1.5
      state.player.boost--
    }
    state.player.x += speed

    // Gestion des sauts
    if (state.player.jumping) {
      if (state.player.jumpHeight < this.config.jumpHeight) {
        state.player.jumpHeight += this.config.jumpSpeed
      } else {
        state.player.jumping = false
      }
    } else if (state.player.jumpHeight > 0) {
      state.player.jumpHeight = Math.max(0, state.player.jumpHeight - this.config.jumpSpeed)
    }

    // Temps d'animation de course
    state.runningTime += 1
  }

  // Mise à jour de la cible
  updateTarget(state: GameState): void {
    state.target.x += this.config.targetSpeed
  }

  // Mise à jour de la caméra
  updateCamera(state: GameState, canvasWidth: number): void {
    const screenLeft = state.camera.x
    const screenRight = state.camera.x + canvasWidth
    const leftmostChar = Math.min(state.player.x, state.target.x)
    const rightmostChar = Math.max(state.player.x, state.target.x)

    // Commencer à suivre si un personnage sort de l'écran
    if (!state.camera.shouldFollow) {
      if (rightmostChar > screenRight - this.config.cameraFollowThreshold ||
          leftmostChar < screenLeft + this.config.cameraFollowThreshold) {
        state.camera.shouldFollow = true
      }
    }

    // Si la caméra doit suivre, centrer entre les personnages
    if (state.camera.shouldFollow) {
      const centerX = (state.player.x + state.target.x) / 2
      const targetCameraX = centerX - canvasWidth / 2
      state.camera.x += (targetCameraX - state.camera.x) * this.config.cameraSpeed
    }
  }

  // Mise à jour des obstacles
  updateObstacles(state: GameState, currentTime: number, lastSpawnTime: { obstacles: number }): void {
    // Spawn nouveaux obstacles
    if (currentTime - lastSpawnTime.obstacles > 6000 && Math.random() < this.config.obstacleSpawnRate) {
      state.obstacles.push({
        x: state.camera.shouldFollow ? state.camera.x + 1000 : 900,
        y: 340,
        width: 30,
        height: 40
      })
      lastSpawnTime.obstacles = currentTime
    }

    // Déplacer et nettoyer les obstacles
    state.obstacles = state.obstacles
      .map(obs => ({ ...obs, x: obs.x - this.config.obstacleSpeed }))
      .filter(obs => state.camera.shouldFollow ? obs.x > state.camera.x - 100 : obs.x > -50)
  }

  // Mise à jour des collectibles (croissants) et pièces
  updateCollectibles(state: GameState, currentTime: number, lastSpawnTime: { hearts: number, coins: number }): void {
    // Croissants (boost)
    if (currentTime - lastSpawnTime.hearts > 4000 && Math.random() < this.config.collectibleSpawnRate) {
      const possibleY = [280, 220, 160, 320]
      const y = possibleY[Math.floor(Math.random() * possibleY.length)]
      if (Math.random() < 0.25) {
        const groupSize = 2 + Math.floor(Math.random() * 3)
        for (let i = 0; i < groupSize; i++) {
          state.collectibles.push({
            x: (state.camera.shouldFollow ? state.camera.x + 1000 : 900) + i * 40,
            y,
            collected: false
          })
        }
      } else {
        state.collectibles.push({
          x: state.camera.shouldFollow ? state.camera.x + 1000 : 900,
          y,
          collected: false
        })
      }
      lastSpawnTime.hearts = currentTime
    }
    // Pièces (points) - apparition plus fréquente
    if (currentTime - lastSpawnTime.coins > 1500 && Math.random() < 0.15) {
      const possibleY = [180, 240, 320]
      const y = possibleY[Math.floor(Math.random() * possibleY.length)]
      state.coins.push({
        x: state.camera.shouldFollow ? state.camera.x + 1000 : 900,
        y,
        collected: false
      })
      lastSpawnTime.coins = currentTime
    }
    // Déplacer et nettoyer croissants
    state.collectibles = state.collectibles
      .map(collectible => ({ ...collectible, x: collectible.x - this.config.collectibleSpeed }))
      .filter(collectible => state.camera.shouldFollow ? collectible.x > state.camera.x - 100 : collectible.x > -50)
    // Déplacer et nettoyer pièces
    state.coins = state.coins
      .map(coin => ({ ...coin, x: coin.x - this.config.collectibleSpeed }))
      .filter(coin => state.camera.shouldFollow ? coin.x > state.camera.x - 100 : coin.x > -50)
  }

  // Détection de collision avec obstacles
  checkObstacleCollisions(state: GameState): boolean {
    return state.obstacles.some(obs =>
      state.player.x < obs.x + obs.width &&
      state.player.x + 28 > obs.x &&
      state.player.y - state.player.jumpHeight < obs.y + obs.height &&
      state.player.y - state.player.jumpHeight + 45 > obs.y
    )
  }

  // Collecte croissants (boost) et pièces (points)
  checkCollectibleCollections(state: GameState): number {
    let scoreGained = 0
    let accelerate = false
    // Croissants = boost
    state.collectibles.forEach(collectible => {
      if (!collectible.collected &&
          state.player.x < collectible.x + 25 &&
          state.player.x + 28 > collectible.x &&
          state.player.y - state.player.jumpHeight < collectible.y + 25 &&
          state.player.y - state.player.jumpHeight + 45 > collectible.y) {
        collectible.collected = true
        accelerate = true
        state.collectEffects.push({
          x: collectible.x + 12,
          y: collectible.y + 12,
          opacity: 1,
          scale: 1,
          time: 0
        })
      }
    })
    // Pièces = points
    state.coins.forEach(coin => {
      if (!coin.collected &&
          state.player.x < coin.x + 20 &&
          state.player.x + 28 > coin.x &&
          state.player.y - state.player.jumpHeight < coin.y + 20 &&
          state.player.y - state.player.jumpHeight + 45 > coin.y) {
        coin.collected = true
        scoreGained += 20 // valeur d'une pièce
        state.collectEffects.push({
          x: coin.x + 10,
          y: coin.y + 10,
          opacity: 1,
          scale: 1,
          time: 0
        })
      }
    })
    // Boost si croissant collecté
    if (accelerate) {
      state.player.boost = (state.player.boost || 0) + 30
    }
    return scoreGained
  }

  // Mise à jour des effets visuels
  updateCollectEffects(state: GameState): void {
    state.collectEffects = state.collectEffects
      .map(effect => ({
        ...effect,
        time: effect.time + 1,
        opacity: Math.max(0, 1 - effect.time / (state.hugAnimation.active ? 60 : 30)),
        scale: effect.scale + (state.hugAnimation.active ? 0.02 : 1 / 20)
      }))
      .filter(effect => effect.opacity > 0)
  }

  // Calcul de la distance entre joueur et cible
  calculateDistance(state: GameState): number {
    return Math.abs(state.target.x - state.player.x)
  }

  // Vérification de la condition de victoire
  checkWinCondition(state: GameState): boolean {
    return this.calculateDistance(state) < this.config.winDistance && !state.hugAnimation.active
  }

  // Démarrer l'animation de câlin
  startHugAnimation(state: GameState): void {
    state.hugAnimation.active = true
    state.hugAnimation.time = 0
    state.hugAnimation.heartsSpawned = false
  }

  // Mise à jour de l'animation de câlin
  updateHugAnimation(state: GameState): boolean {
    if (!state.hugAnimation.active) return false

    state.hugAnimation.time += 1

    // Rapprocher les personnages
    if (state.hugAnimation.time <= 60) {
      const progress = state.hugAnimation.time / 60
      const targetX = (state.player.x + state.target.x) / 2

      state.player.x += (targetX - 5 - state.player.x) * 0.1
      state.target.x += (targetX + 5 - state.target.x) * 0.1
    }

    // Faire apparaître des cœurs
    if (state.hugAnimation.time === 30 && !state.hugAnimation.heartsSpawned) {
      state.hugAnimation.heartsSpawned = true
      const centerX = (state.player.x + state.target.x) / 2
      const centerY = (state.player.y + state.target.y) / 2

      for (let i = 0; i < 8; i++) {
        const angle = (i * Math.PI * 2) / 8
        const radius = 40 + Math.random() * 30
        state.collectEffects.push({
          x: centerX + Math.cos(angle) * radius,
          y: centerY + Math.sin(angle) * radius - 20,
          opacity: 1,
          scale: 1.5,
          time: 0
        })
      }
    }

    // Terminer l'animation
    return state.hugAnimation.time >= 120
  }

  // Vérification de game over
  checkGameOverCondition(state: GameState, canvasWidth: number): boolean {
    if (state.hugAnimation.active) return false

    const screenRightEdge = state.camera.shouldFollow ?
      state.camera.x + canvasWidth + 100 :
      canvasWidth + 100

    return state.target.x > screenRightEdge
  }

  // Créer un état initial
  createInitialState(playerStartX: number = 10, targetStartX: number = 200): GameState {
    return {
      player: { x: playerStartX, y: 320, jumping: false, jumpHeight: 0, boost: 0 },
      target: { x: targetStartX, y: 320, jumping: false, jumpHeight: 0 },
      obstacles: [],
      collectibles: [],
      coins: [],
      collectEffects: [],
      distance: Math.abs(targetStartX - playerStartX),
      score: 0,
      camera: { x: 0, shouldFollow: false },
      hugAnimation: { active: false, time: 0, heartsSpawned: false },
      runningTime: 0
    }
  }
}
